/*
Author: Chathuraka Mallawa Arachchi
Date: 25/09/2024
Copyright © 2024 Infix!. All rights reserved.
*/

import Errors from "@/components/Atoms/Error";
import InputField from "@/components/Atoms/InputField";
import InputLabel from "@/components/Atoms/InputLabel";
import {useUrlType} from "@/components/hooks";
import {RootState} from "@/reducers/store";
import {useValidate} from "@/state/survey/formSlice";
import {Utility,Validate} from "@/utils";
import {ReactNode,useEffect,useMemo,useRef,useState} from "react";
import {useSelector} from "react-redux";
import {IComponentMetaData} from "../../SurveyModel/types/commonTypes";
import {INode} from "../../SurveyModel/types/nodeTypes";

interface IMDASI4QRiskScore {
    node: INode
    children: ReactNode
    name: string
    disabled: boolean
    metaData?: IComponentMetaData
    repeatedElement?:boolean
    onBlur?:()=> void
    type?:string
}

const MDASI4QRiskScore:React.FC<IMDASI4QRiskScore>  = (props:IMDASI4QRiskScore) => {
    const {node, children, name, disabled, repeatedElement,  type="Text" } = props;
    const form = useSelector((state:RootState) => state.form);
    const [questionName, setQuestionName] = useState('') 
    const validateInputField = useValidate();
    const hasExecuted = useRef(false);
    const { state = {} } = form[node.name];
    const [inputValue, setInputValue ] = useState(form[node.name].value);
    const {isPatient} = useUrlType();
    const [alertExp, setAlertExp] = useState('') 
    const [nodesWithValidations, setNodesWithValidations] = useState<string[]>([]);

    useEffect(()=>{
        if(form[node.name].value !== inputValue){
            setInputValue(form[node.name].value )
        }
    }, [form, inputValue]);
   
    const handleOnChange = () => {
    }
    
    const handleOnBlur = () => {
    }
    
    useEffect(() => {

        if (node?.validations) {
            const fieldWidthConfig = node?.validations.find(
                (validation) => validation.type === 'CompareEmptyList'
            )
            const fieldWidthValue = fieldWidthConfig && fieldWidthConfig.field
                ? JSON.parse(fieldWidthConfig.field.replace(/'/g, '"'))
                : ['']

                setNodesWithValidations(fieldWidthValue)
        }
    
    }, [node?.validations]);


    const watchedValidationValue = useMemo(() => {
        let score = 0;
        nodesWithValidations.map((key: string) =>{
        
            switch(key) {
                case 'CanClimbFlightOfStairs' : {
                    if(form['CanClimbFlightOfStairs'].value === "Yes"){
                        score = score + 5.5
                    }
                       
                }
                break;
                case 'CanDoHeavyWorkAroundHouse' : {
                    if(form['CanDoHeavyWorkAroundHouse'].value === "Yes"){
                        score = score + 8
                    }  
                }
                break;
                case 'CanDoGardenWork' : {
                    if(form['CanDoGardenWork'].value === "Yes"){
                        score = score + 4.5
                    }
                    
                       
                }
                break;
                case 'CanDoStrenuousSports' : {
                    if(form['CanDoStrenuousSports'].value === "Yes"){
                        score = score + 7.5
                    }
                     
                }
                break;
                default: 
            }
            
        })
    return score
    } , [form, nodesWithValidations]);

    useEffect(() => {
        if(form['CanClimbFlightOfStairs'].value === "No" && form['CanDoHeavyWorkAroundHouse'].value === "No" && form['CanDoGardenWork'].value === "No" && form['CanDoStrenuousSports'].value === "No"){
            validateInputField(node.name,  `0 / High`, true);
        }

    },[form['CanClimbFlightOfStairs'].value, form['CanDoHeavyWorkAroundHouse'].value,form['CanDoGardenWork'].value, form['CanDoStrenuousSports'].value,form[node.name].value ])


    useEffect(() => {
       let value: string = ''
    
        if(watchedValidationValue <= 9){
            value = `${watchedValidationValue} / High`
        }
        else if(watchedValidationValue > 9 && watchedValidationValue < 20){
             value = `${watchedValidationValue} / Moderate`
        }
        else if(watchedValidationValue >= 20){
             value = `${watchedValidationValue} / Low`
        }
        if (watchedValidationValue === 0 ) {
            if(form['CanClimbFlightOfStairs'].value === "" || form['CanDoHeavyWorkAroundHouse'].value === "" || form['CanDoGardenWork'].value === "" || form['CanDoStrenuousSports'].value === ""){
                validateInputField(node.name, '', true);
            }
            else {
                validateInputField(node.name, value, true);
            }
         
        }else {
            if(form['CanClimbFlightOfStairs'].value !== "" && form['CanDoHeavyWorkAroundHouse'].value !== "" && form['CanDoGardenWork'].value !== "" && form['CanDoStrenuousSports'].value !== ""){
                validateInputField(node.name, value, true);
            }
     
        }
    
    }, [ nodesWithValidations, watchedValidationValue]); 

    useEffect(() => {
        node?.alerts?.map((alert: any) => {
            if (Utility.evaluateManually(form, alert.expression)) {
                setAlertExp(alert.expression)
            }
        });
    }, [node?.alerts, form[node.name].value]);

    useEffect(()=>{
        if(!isPatient && name && form[node.name] && questionName != name && !hasExecuted.current){ //poa only
            validateInputField(name, form[node.name].value, true);
            setQuestionName(name)
            hasExecuted.current = true;
        }
    }, [form, questionName, node, isPatient, name,validateInputField ])
    
    return (
        <div className={ `risk-score-wrap 
        ${Utility.getClassForWidthPx(node)}  
        ${Utility.getClassForWidth(node)} 
        question-bottom-space-1
        ${node.name}
         ${Validate.isInvalid(state) ? 'error': ''} 
         ${!repeatedElement ? `user-input flex-1 ${!isPatient ? 'poa-input': '' }
          input-border-top` : ''}${ alertExp && Utility.evaluateManually(form, alertExp) ? ' red-strip':''}` } >
            <div className="user-input-body">

            <InputLabel label={node.title} labelClasses="input-label"></InputLabel> 
            <div className="fixed-128">          
        {node.subTitle  ? <InputLabel type="subTitle" label={node.subTitle } labelClasses="input-label"></InputLabel> : ''}
            {/* <SingleChoice propClasses={'single-chocie-choices flex'} value={form[node.name].value} options={node.options} name={node.name} onChange={handleChange}/> */}
            <InputField showErrors={false} readOnly={true}  disabled={disabled} form={form} isInValid = {Validate.isInvalid(state)} propClasses={'single-line-text flex'}  type={type} value={form[node.name].value}  name={name} onChange={handleOnChange} onBlur={handleOnBlur} />
            <div>{children}</div>
            </div>
            {form  ?<Errors fieldName={name} validation={form[name] ? form[name] : undefined}/> : '' }
            </div>
            
        </div>
    )
}

export default MDASI4QRiskScore;